// bluaIQ — P2: Contract Signing Page
// Public, token-backed page for contractors to review and sign a
// contract emailed to them via /contracts/:id/send-for-signature.
//
// Auth model:
//   * The route at /contracts/:contractId/sign is public (App.tsx).
//   * The fetch hits GET /api/contracts/:id?token=<signToken>,
//     which the server whitelist (src/middleware/auth.ts) admits
//     without a Bearer. The route validates the token against
//     Contract.signToken; mismatches return 404.
//
// Shape:
//   * The API returns the Prisma Contract row (with `project`
//     included). Earlier versions of this page defined a
//     hand-rolled shape (`title`, `scopeItems[]`, `terms`,
//     `termsEs`, …) that did NOT match the API — the render
//     crashed on `contract.scopeItems.map()` and ErrorBoundary
//     surfaced "Algo salió mal". This file now reads only fields
//     that actually exist on Contract, with null guards on every
//     optional column.

import { apiFetch } from '../utils/api'
import { formatDateOnly } from '../utils/dateOnly'
import { Component, useState, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, CheckCircle, AlertTriangle } from 'lucide-react'
import SignaturePad from './SignaturePad'
import { ContractSignErrorNotice } from './ContractSignErrorNotice'
import { ContractSignInvalidLink } from './ContractSignInvalidLink'
import { classifySignError, type SignErrorKey } from '../shared/contractSignErrorCopy'

/**
 * A signing failure, carrying ONLY a curated-copy key.
 *
 * The `message` is deliberately the key itself — a stable internal token, never
 * the server's text — so that even a future `error.message` render (the exact
 * bug this replaces) cannot leak system copy to a signer.
 */
class SignRequestError extends Error {
  readonly copyKey: SignErrorKey
  constructor(copyKey: SignErrorKey) {
    super(copyKey)
    this.name = 'SignRequestError'
    this.copyKey = copyKey
  }
}

// ── TYPES ─────────────────────────────────────────

interface DrawScheduleItem {
  pct: number
  condition: string
  dueDate?: string | null
}

// Mirrors prisma/schema.prisma → model Contract, plus the
// included `project` relation. Everything except `id` is treated
// as `unknown` because the API can change shape across versions
// or return a stale cached response; we normalize via toSafeContract
// before the render reads anything.
interface RawContract {
  id?: unknown
  contractorName?: unknown
  trade?: unknown
  totalAmount?: unknown
  state?: unknown
  scopeOfWork?: unknown
  paymentTerms?: unknown
  timelineDays?: unknown
  startDate?: unknown
  penalties?: unknown
  drawSchedule?: unknown
  signedAt?: unknown
  signerName?: unknown
  status?: unknown
  project?: unknown
}

// Strict view model used by the render. Every field has a
// guaranteed safe type; nothing in the JSX is allowed to do
// `.toLocaleString()`, `.map()`, `.trim()` etc. on an unknown.
interface SafeContract {
  id: string
  contractorName: string
  trade: string
  totalAmount: number
  scopeOfWork: string | null
  paymentTerms: string | null
  timelineDays: number | null
  startDate: string | null
  penalties: string | null
  drawSchedule: DrawScheduleItem[]
  signedAt: string | null
  signerName: string | null
  status: string
  projectName: string | null
}

function asString(v: unknown): string | null {
  if (typeof v !== 'string') return null
  return v.trim().length > 0 ? v : null
}

function asFiniteNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return null
}

function asDrawSchedule(v: unknown): DrawScheduleItem[] {
  if (!Array.isArray(v)) return []
  const out: DrawScheduleItem[] = []
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') continue
    const rec = raw as Record<string, unknown>
    const pct = asFiniteNumber(rec.pct)
    const condition = asString(rec.condition)
    const dueDate = asString(rec.dueDate)
    out.push({
      pct: pct == null ? 0 : pct,
      condition: condition ?? '—',
      dueDate: dueDate,
    })
  }
  return out
}

/**
 * Defensive normalizer: any API-shaped object (even partial /
 * adversarial) becomes a `SafeContract` whose every field has the
 * type the JSX expects. The render path can read these values
 * without optional chaining or runtime guards.
 */
export function toSafeContract(raw: unknown): SafeContract | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as RawContract
  const id = asString(r.id)
  if (!id) return null
  return {
    id,
    contractorName: asString(r.contractorName) ?? '—',
    trade: asString(r.trade) ?? '—',
    totalAmount: asFiniteNumber(r.totalAmount) ?? 0,
    scopeOfWork: asString(r.scopeOfWork),
    paymentTerms: asString(r.paymentTerms),
    timelineDays: asFiniteNumber(r.timelineDays),
    startDate: asString(r.startDate),
    penalties: asString(r.penalties),
    drawSchedule: asDrawSchedule(r.drawSchedule),
    signedAt: asString(r.signedAt),
    signerName: asString(r.signerName),
    status: asString(r.status) ?? 'DRAFT',
    projectName:
      r.project && typeof r.project === 'object'
        ? asString((r.project as Record<string, unknown>).name)
        : null,
  }
}

// ── LOCAL ERROR BOUNDARY ──────────────────────────
// Catches render-time crashes inside the sign-page subtree so the
// recipient sees a friendly, sign-page-scoped fallback instead of
// the global "Algo salió mal" screen from src/components/
// ErrorBoundary.tsx. Sentry is the global boundary's job — we
// only need to keep the recipient out of the app-error screen.
interface SignBoundaryProps {
  isEs: boolean
  children: ReactNode
}
interface SignBoundaryState {
  hasError: boolean
}
class SignPageBoundary extends Component<SignBoundaryProps, SignBoundaryState> {
  state: SignBoundaryState = { hasError: false }
  static getDerivedStateFromError(): SignBoundaryState {
    return { hasError: true }
  }
  componentDidCatch(error: Error) {
    // Keep a console line so it shows up in production logs /
    // Sentry's automatic console-breadcrumb capture without
    // crossing into the app-level ErrorBoundary.
    console.error('[ContractSignPage] render error:', error?.message)
  }
  render() {
    if (this.state.hasError) {
      const { isEs } = this.props
      return (
        <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-5">
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-8 text-center max-w-md">
            <AlertTriangle size={48} className="mx-auto text-[#F5A623] mb-4" />
            <h2 className="text-[18px] font-[900] text-[#1C1A17] mb-2">
              {isEs ? 'No pudimos mostrar el contrato' : "We couldn't show this contract"}
            </h2>
            <p className="text-[12px] font-[600] text-[#9E9890]">
              {isEs
                ? 'Recargue la página o solicite a su constructor que le envíe el enlace de nuevo.'
                : 'Reload the page or ask your builder to send the link again.'}
            </p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// ── COMPONENT ─────────────────────────────────────

export default function ContractSignPage() {
  const { i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  // The signing link carries the contract's language as ?lang= (set by the
  // builder at send time). Prefer it over the contractor's device language:
  // the app force-defaults to Spanish (see src/i18n.ts), so an English contract
  // opened on a fresh device would otherwise render every signing state —
  // including the success screen — in Spanish. Falls back to the app language
  // for older links that have no lang param.
  const langParam = searchParams.get('lang')
  const isEs = langParam === 'es' ? true : langParam === 'en' ? false : i18n.language === 'es'
  return (
    <SignPageBoundary isEs={isEs}>
      <ContractSignPageInner isEs={isEs} />
    </SignPageBoundary>
  )
}

function ContractSignPageInner({ isEs }: { isEs: boolean }) {
  const { contractId } = useParams()
  const [searchParams] = useSearchParams()
  const signToken = searchParams.get('token')

  const [signerName, setSignerName] = useState('')
  const [signerNamePrefilled, setSignerNamePrefilled] = useState(false)
  const [signatureData, setSignatureData] = useState<string | null>(null)
  const [signed, setSigned] = useState(false)

  // Public fetch — never sends a Bearer. The token query param
  // is the entire credential. The queryFn carefully (1) checks
  // res.ok, (2) checks the response is JSON before parsing, and
  // (3) returns `null` for "invalid link" (instead of throwing)
  // so the next render branch shows a friendly screen rather
  // than churning into the local error boundary.
  const { data: raw, isLoading, isError } = useQuery<unknown>({
    queryKey: ['contract-sign', contractId, signToken],
    queryFn: async () => {
      if (!contractId) throw new Error('missing_contract_id')
      const url = signToken
        ? `/api/contracts/${contractId}?token=${encodeURIComponent(signToken)}`
        : `/api/contracts/${contractId}`
      const r = await apiFetch(url)
      if (!r.ok) {
        // Invalid token / 404 / 5xx → resolve to null and let
        // the render show the friendly invalid-link screen.
        return null
      }
      const ct = r.headers.get('content-type') || ''
      if (!ct.toLowerCase().includes('application/json')) {
        return null
      }
      try {
        return await r.json()
      } catch {
        return null
      }
    },
    retry: false,
    enabled: !!contractId,
  })
  const contract = toSafeContract(raw)

  // Prefill the signer-name field with the contract's contractorName
  // once. The user can still edit it (e.g. if the actual signer is
  // a different authorized representative). Guarded by
  // `signerNamePrefilled` so a later typo + re-render doesn't keep
  // resetting the field back to the contractor name.
  useEffect(() => {
    if (signerNamePrefilled) return
    if (!contract) return
    const name = contract.signerName || contract.contractorName
    if (name && name !== '—') {
      setSignerName(name)
      setSignerNamePrefilled(true)
    }
  }, [contract, signerNamePrefilled])

  const signMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/contracts/${contractId}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName,
          signatureImage: signatureData,
          signedAt: new Date().toISOString(),
          token: signToken,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        // Classify on the server's STABLE MACHINE CODE — never on its prose —
        // and carry only a COPY KEY. The `error` string is a log line for
        // operators; it never reaches the view, and never reaches component
        // state at all, so it cannot leak through `.message`.
        throw new SignRequestError(classifySignError(res.status, err?.code))
      }
      return res.json()
    },
    onSuccess: () => setSigned(true),
  })

  const handleSign = () => {
    if (!signerName || !signatureData) return
    signMutation.mutate()
  }

  const today = new Date().toLocaleDateString(isEs ? 'es-MX' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // ── Loading ─────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-5">
        <div className="animate-pulse w-full max-w-3xl">
          <div className="h-32 bg-[#ECEAE4] rounded-xl mb-4" />
          <div className="h-48 bg-[#ECEAE4] rounded-xl mb-4" />
          <div className="h-24 bg-[#ECEAE4] rounded-xl" />
        </div>
      </div>
    )
  }

  // ── Invalid / ALREADY USED / expired link, or fetch error ──────
  // Extracted to a prop-only component so this path — the one a re-opened
  // (already-signed) link actually lands on — is provable in a real browser at
  // 390px instead of only matched in source. See ContractSignInvalidLink.tsx.
  if (isError || !contract) {
    return <ContractSignInvalidLink isEs={isEs} />
  }

  // ── Already signed ──────────────────────────────
  // PR-NEW: the previous copy promised "a copy will be sent to
  // all parties", but the server only fires a best-effort
  // builder-notify email (no contractor copy) and swallows
  // failures silently. The new copy only commits to what the
  // backend actually does today: persist the signature and let
  // the builder review/countersign.
  if (signed || contract.signedAt) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-5">
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-8 text-center max-w-md">
          <CheckCircle size={48} className="mx-auto text-[#22C55E] mb-4" />
          <h2 className="text-[18px] font-[900] text-[#1C1A17] mb-2">
            {isEs ? 'Contrato Firmado' : 'Contract Signed'}
          </h2>
          <p className="text-[12px] font-[600] text-[#9E9890]">
            {isEs
              ? 'Contrato firmado correctamente. Su constructor lo revisará y firmará si es necesario.'
              : 'Contract signed successfully. The builder will review and countersign if required.'}
          </p>
          <div className="mt-4 text-[11px] font-[700] text-[#9E9890]">
            {(signerName || contract.signerName || contract.contractorName)} · {today}
          </div>
        </div>
      </div>
    )
  }

  // ── Defensive shape derivation ──────────────────
  // toSafeContract has already normalized every field; here we
  // only translate the display name.
  const projectName = contract.projectName ?? (isEs ? 'Proyecto' : 'Project')
  const draws = contract.drawSchedule // already typed Array
  const contractTitle = isEs
    ? `Contrato de Subcontrato — ${contract.trade}`
    : `Subcontractor Agreement — ${contract.trade}`

  return (
    <div className="min-h-screen bg-[#FAFAF8] p-5">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText size={20} className="text-[#06539B]" />
            <h1 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
              {contractTitle}
            </h1>
          </div>
          <div className="grid grid-cols-2 gap-4 text-[11px] font-[600]">
            <div>
              <span className="text-[#9E9890]">{isEs ? 'Contratista' : 'Contractor'}:</span>
              <span className="ml-2 font-[800] text-[#1C1A17]">{contract.contractorName}</span>
            </div>
            <div>
              <span className="text-[#9E9890]">{isEs ? 'Especialidad' : 'Trade'}:</span>
              <span className="ml-2 font-[800] text-[#1C1A17]">{contract.trade}</span>
            </div>
            <div>
              <span className="text-[#9E9890]">{isEs ? 'Proyecto' : 'Project'}:</span>
              <span className="ml-2 font-[800] text-[#1C1A17]">{projectName}</span>
            </div>
            <div>
              <span className="text-[#9E9890]">{isEs ? 'Monto del contrato' : 'Contract Amount'}:</span>
              <span className="ml-2 font-[900] text-[#1C1A17]">
                ${contract.totalAmount.toLocaleString()}
              </span>
            </div>
            {contract.timelineDays != null && (
              <div>
                <span className="text-[#9E9890]">{isEs ? 'Cronograma' : 'Timeline'}:</span>
                <span className="ml-2 font-[800] text-[#1C1A17]">
                  {contract.timelineDays} {isEs ? 'días' : 'days'}
                </span>
              </div>
            )}
            {contract.startDate && (
              <div>
                <span className="text-[#9E9890]">{isEs ? 'Inicio' : 'Start date'}:</span>
                <span className="ml-2 font-[800] text-[#1C1A17]">
                  {formatDateOnly(contract.startDate, isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: 'numeric', day: 'numeric' })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Scope of Work */}
        {contract.scopeOfWork && (
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-2">
            <h3 className="text-[13px] font-[800]">{isEs ? 'Alcance del Trabajo' : 'Scope of Work'}</h3>
            <p className="text-[12px] font-[600] text-[#1C1A17] whitespace-pre-wrap">
              {contract.scopeOfWork}
            </p>
          </div>
        )}

        {/* Payment Terms */}
        {contract.paymentTerms && (
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-2">
            <h3 className="text-[13px] font-[800]">{isEs ? 'Términos de Pago' : 'Payment Terms'}</h3>
            <p className="text-[12px] font-[600] text-[#1C1A17] whitespace-pre-wrap">
              {contract.paymentTerms}
            </p>
          </div>
        )}

        {/* Draw Schedule */}
        {draws.length > 0 && (
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-2">
            <h3 className="text-[13px] font-[800]">{isEs ? 'Calendario de Pagos' : 'Draw Schedule'}</h3>
            <div className="space-y-1.5">
              {draws.map((d, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 py-1.5 border-b border-[#F2F0EB] last:border-0"
                >
                  <span className="text-[10px] font-[800] text-[#06539B] w-[40px]">
                    {Number(d?.pct ?? 0)}%
                  </span>
                  <span className="text-[11px] font-[600] flex-1">{d?.condition ?? '—'}</span>
                  <span className="text-[12px] font-[800] text-[#1C1A17]">
                    ${Math.round(contract.totalAmount * Number(d?.pct ?? 0) / 100).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Penalties */}
        {contract.penalties && (
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-2">
            <h3 className="text-[13px] font-[800]">{isEs ? 'Penalidades y Garantía' : 'Penalties & Warranty'}</h3>
            <p className="text-[12px] font-[600] text-[#1C1A17] whitespace-pre-wrap">
              {contract.penalties}
            </p>
          </div>
        )}

        {/* Signature section */}
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-4">
          <h3 className="text-[13px] font-[800]">{isEs ? 'Firma Digital' : 'Digital Signature'}</h3>

          <div className="flex items-center gap-2 p-3 bg-[#EEF2FF] rounded-lg">
            <AlertTriangle size={14} className="text-[#06539B] flex-shrink-0" />
            <p className="text-[10px] font-[600] text-[#06539B]">
              {isEs
                ? 'Al firmar, acepta los términos del contrato arriba detallados.'
                : 'By signing, you agree to the contract terms detailed above.'}
            </p>
          </div>

          {/* Signer name */}
          <div>
            <label className="text-[12px] font-[700] text-[#9E9890] block mb-1">
              {isEs ? 'Nombre legal completo del firmante' : 'Full legal name of signer'}
            </label>
            <input
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder={contract.contractorName}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <p className="mt-1 text-[10px] font-[600] text-[#9E9890]">
              {isEs
                ? 'Usa el nombre legal completo de la persona autorizada para firmar este contrato.'
                : 'Use the full legal name of the person authorized to sign this contract.'}
            </p>
          </div>

          {/* Date */}
          <div>
            <label className="text-[12px] font-[700] text-[#9E9890] block mb-1">
              {isEs ? 'Fecha' : 'Date'}
            </label>
            <div className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] text-[#9E9890] bg-[#F2F0EB]">
              {today}
            </div>
          </div>

          {/* Signature pad */}
          <div>
            <label className="text-[12px] font-[700] text-[#9E9890] block mb-1">
              {isEs ? 'Firma (dibuje abajo)' : 'Signature (draw below)'}
            </label>
            <SignaturePad onSignature={setSignatureData} />
          </div>

          {signMutation.isError && (
            <ContractSignErrorNotice
              copyKey={
                signMutation.error instanceof SignRequestError
                  ? signMutation.error.copyKey
                  : 'unknown' /* network/parse failure — curated generic, never raw */
              }
              isEs={isEs}
            />
          )}

          {/* Sign button */}
          <button
            onClick={handleSign}
            disabled={!signerName || !signatureData || signMutation.isPending}
            className="w-full bg-[#06539B] text-white text-[12px] font-[800] py-3.5 rounded-full disabled:opacity-40"
          >
            {signMutation.isPending ? '...' : (isEs ? 'Firmar Contrato' : 'Sign Contract')}
          </button>
        </div>
      </div>
    </div>
  )
}

// SignaturePad now lives in src/components/SignaturePad.tsx so
// the Phase-2 builder/owner countersign modal can reuse it.
